import { resolveFleetIdentity } from '@/lib/mqtt/legacy/fleetIdentity';
import { partnerTenantFromPayload } from '@/lib/tenancy';
/**
 * `v1/output/aircraft/{sn}/networkid` — ASTM F3411 Network Remote ID.
 *
 * Replaces the legacy `networkid/{UAS_ID}` topic (ticket 86e2jbe3d). This
 * is the identity + position stream and the baseline envelope for a drone:
 * `telemetry.ts` merges airframe state on top of whatever this produces.
 *
 * Verified against sandbox 2026-07-30 (SIMTL70 + SIMTL80). Differences
 * from the FLYsafe MQTT doc that this parser deliberately absorbs:
 *   - `uaType` / `uasIdType` are integers on the wire, enums in the doc.
 *   - accuracy fields are plain metres (0.3), enums in the doc (doc OI-12).
 *   - `operationalStatus` is title-case ("Airborne"), upper-case in the doc.
 *   - `ext.waypoints` are flat `[lat, lng]` pairs, `[{seq,lat,lng,altM}]`
 *     in the doc.
 * Every one of those is accepted in both forms so a backend correction
 * can't blank the fleet.
 */
import {
  type TelemetryEnvelope,
  TelemetryEnvelope as TelemetryEnvelopeSchema,
} from '@/lib/mqtt/schemas/telemetry';
import {
  type FieldRule,
  accuracyMetres,
  applyRules,
  asNumber,
  asText,
  isoUtc,
  recordAccepted,
  recordParseFailure,
  speedAccuracyMps,
} from './registry';

const TOPIC_KIND = 'networkid';

const M_PER_DEG_LAT = 111_320;
const M_TO_FT = 3.28084;
const mPerDegLng = (lat: number) => 111_320 * Math.cos((lat * Math.PI) / 180);

/**
 * F3411 `operationalStatus` → our health scale, for the emergency states ONLY.
 *
 * These are not the same axis. `operationalStatus` is flight phase
 * (Undeclared / Ground / Airborne / Emergency / RemoteID System Failure);
 * `ops_status` is Normal / Degraded / Non-Normal health. An aircraft can
 * be airborne and degraded at the same time, so "Airborne" says nothing
 * about health and must NOT become "Normal" — that would be us inventing
 * a health signal the broadcast never made.
 *
 * Only the two states that are explicitly failures map across. Everything
 * else returns null and the Ops Status tile stays "—" until a real health
 * source exists. The verbatim phase is always kept on `flight_phase`.
 */
function opsStatusFromPhase(v: unknown): TelemetryEnvelope['ops_status'] {
  const s = asText(v)?.toLowerCase().replace(/[\s-]/g, '_');
  if (s === 'emergency' || s === 'remoteid_system_failure') return 'Non-Normal';
  return null;
}

/** Emergency flag for the Remote ID panel. Null when no status was
 *  broadcast — absence of a declaration is not a declaration of safety. */
function emergencyStatus(v: unknown): string | null {
  const s = asText(v);
  if (!s) return null;
  return opsStatusFromPhase(s) === 'Non-Normal' ? 'Emergency' : 'None';
}

/** GPS-lock heuristic — INHERITED from the legacy `networkid` adapter, not
 *  introduced here, and kept so both schemas render identically. F3411
 *  exposes no discrete lock state, so horizontal accuracy stands in:
 *  <15 m Acquired, <50 m Acquiring. This is a proxy, not a broadcast
 *  value; if the Navigation panel is ever taken as authoritative it needs
 *  a real source. Same applies to the `gps_hdop` divide-by-5 below. */
function gpsLock(v: unknown): TelemetryEnvelope['gps_lock'] {
  const m = accuracyMetres(v);
  if (m == null) return 'Lost';
  if (m < 15) return 'Acquired';
  if (m < 50) return 'Acquiring';
  return 'Lost';
}

/** `ext.waypoints` are `[lat, lng]`; the OI envelope stores GeoJSON order.
 *  This transpose is the single reason waypoints and `operational_volume`
 *  must not share a coordinate helper — the latter is already `[lng, lat]`. */
function waypointsToLngLat(raw: unknown): [number, number][] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: [number, number][] = [];
  for (const wp of raw) {
    // Flat pair `[lat, lng]` (sandbox) or object `{lat, lng}` (doc shape).
    if (Array.isArray(wp) && wp.length >= 2) {
      const lat = asNumber(wp[0]);
      const lng = asNumber(wp[1]);
      if (lat == null || lng == null) continue;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
      out.push([lng, lat]);
      continue;
    }
    if (wp && typeof wp === 'object') {
      const o = wp as Record<string, unknown>;
      const lat = asNumber(o.lat);
      const lng = asNumber(o.lng ?? o.lon);
      if (lat == null || lng == null) continue;
      out.push([lng, lat]);
    }
  }
  return out.length > 0 ? out : null;
}

/** Fields consumed by the envelope build below rather than by a `to`
 *  target, or knowingly not consumed at all. Listed so they stay out of
 *  the unmapped report without being silently ignored. */
const IGNORED = [
  // Position + identity are read directly in `build` (they are required
  // fields and need cross-field validation, not a per-field rule).
  'uasId',
  'latitude',
  'longitude',
  // Both consumed by the agl/atl split in `build` — `heightType` decides
  // which slot `height` lands in, so neither can be a standalone rule.
  'height',
  'heightType',
  // Structural parent of the ext.* rules.
  'ext',
  // Mapped onto the OI envelope by `remoteIdToOiUpdate`, not telemetry.
  'ext.pilotFullName',
  'ext.missionName',
  'ext.missionLocation',
  'ext.waypoints',
  // Documented but absent from the sandbox publisher. Listed so that if
  // they start arriving they don't read as a surprise — they still need
  // wiring, tracked as a follow-up rather than reported as unknown.
  'operatorId',
  'operatorIdType',
  'operatorLatitude',
  'operatorLongitude',
  'operatorAltitude',
  'areaCount',
  'areaRadius',
  'areaCeiling',
  'areaFloor',
  'uaClassification',
] as const;

const RULES: readonly FieldRule<TelemetryEnvelope>[] = [
  { from: 'ext.callsign', to: 'callsign', via: asText },
  { from: 'direction', to: 'heading', via: (v) => asNumber(v) },
  { from: 'speed', to: 'groundspeed', via: asNumber },
  { from: 'verticalSpeed', to: 'climbRate', via: asNumber },
  { from: 'geodeticAltitude', to: 'asl', via: asNumber },
  {
    from: 'operationalStatus',
    to: 'ops_status',
    via: opsStatusFromPhase,
    note: 'flight phase → health scale; verbatim value kept on flight_phase',
  },
  { from: 'operationalStatus', to: 'flight_phase', via: asText },
  { from: 'heightType', to: 'height_type', via: asText },
  { from: 'horizontalAccuracy', to: 'gps_lock', via: gpsLock },
  {
    from: 'horizontalAccuracy',
    to: 'gps_hdop',
    via: (v) => {
      const m = accuracyMetres(v);
      // Loose proxy: accuracy metres ÷ 5 approximates an HDOP scale.
      return m == null ? null : m / 5;
    },
  },
  { from: 'verticalAccuracy', to: 'vert_accuracy_m', via: accuracyMetres },
  { from: 'speedAccuracy', to: 'speed_accuracy_mps', via: speedAccuracyMps },
  { from: 'timestampAccuracy', to: 'timestamp_accuracy_s', via: asNumber },
  { from: 'timestamp', to: 'received_at', via: isoUtc },
  { from: 'selfIdText', to: 'self_id_text', via: asText },
  { from: 'ext.trackOrigin', to: 'track_origin', via: asText },
  { from: 'uasIdType', to: 'drone_type' as never, via: () => undefined },
  { from: 'uaType', to: 'drone_type' as never, via: () => undefined },
  // Cross-tenant provenance. Consumed by `partnerTenantFromPayload` in the
  // build below (the two fields only mean something read together), listed
  // here so they don't surface as unmapped.
  { from: 'ext.sourceTenant', via: () => undefined },
  { from: 'sourceTenant', via: () => undefined },
  { from: 'tenantId', via: () => undefined },
];

/**
 * Build the baseline telemetry envelope for a drone.
 *
 * Returns null only when the message lacks the three things that make it
 * a usable position report — id, latitude, longitude. Every other absence
 * is tolerated and left null for the telemetry topic to backfill.
 */
export function remoteIdToTelemetry(raw: unknown): TelemetryEnvelope | null {
  if (raw == null || typeof raw !== 'object') {
    recordParseFailure(TOPIC_KIND, 'payload is not an object');
    return null;
  }
  const p = raw as Record<string, unknown>;

  const droneId = asText(p.uasId);
  const lat = asNumber(p.latitude);
  const lng = asNumber(p.longitude);
  if (!droneId || lat == null || lng == null) {
    recordParseFailure(TOPIC_KIND, 'missing uasId / latitude / longitude');
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    recordParseFailure(TOPIC_KIND, `coordinates out of range: ${lat},${lng}`);
    return null;
  }

  const { mapped } = applyRules<TelemetryEnvelope>(TOPIC_KIND, p, RULES, IGNORED);

  const identity = resolveFleetIdentity(droneId, asNumber(p.uaType));
  const rawHeading = mapped.heading as number | null | undefined;
  // F3411 sends direction 361 for "unknown"; both it and a missing value fall back to 0°
  // for the marker, but must not be shown to a pilot as a real heading.
  const headingKnown = rawHeading != null && rawHeading !== 361;
  const heading = (((headingKnown ? rawHeading : 0) as number) + 360) % 360;
  const speedMps = (mapped.groundspeed as number | null) ?? 0;

  // `heightType` decides which altitude slot `height` belongs in. Before
  // this field existed the adapter mirrored AGL into ATL, so the Overview
  // tab showed the same number twice.
  const heightM = asNumber(p.height);
  const heightType = asText(p.heightType)?.toUpperCase() ?? 'AGL';
  const isAgl = heightType !== 'TAKEOFF_RELATIVE';
  const aglM = isAgl ? heightM : null;
  const atlM = isAgl ? null : heightM;

  // T+60 s dead-reckoning. `inside` stays null — this topic has the
  // position but not the OI polygon; the drones store fills it in via
  // deriveOiContainment() once both halves exist.
  const headingRad = (heading * Math.PI) / 180;
  const aheadM = speedMps * 60;
  const pred1min =
    speedMps > 0
      ? {
          lat: lat + (aheadM * Math.cos(headingRad)) / M_PER_DEG_LAT,
          lng: lng + (aheadM * Math.sin(headingRad)) / mPerDegLng(lat),
          inside: null,
        }
      : { lat, lng, inside: null };

  const horizAcc = accuracyMetres(p.horizontalAccuracy);
  const receivedAt = (mapped.received_at as string | null) ?? new Date().toISOString();

  // Cross-tenant provenance. This is the identity topic, so it is the one
  // that gets to declare an aircraft foreign — but only positively: when
  // the payload says nothing, the fields are left off entirely rather than
  // written as 'internal'/null. `mergeNonNull` upstream and `mergeTelemetry`
  // in the store both keep a previous non-null value over an absent one, so
  // omitting means a partner aircraft can't be silently reclassified as ours
  // by one message that happens to lack `sourceTenant`.
  const partnerTenant = partnerTenantFromPayload(p);

  const built: TelemetryEnvelope = {
    drone_id: droneId,
    callsign: (mapped.callsign as string | null) ?? identity.callsign ?? droneId,
    drone_model: identity.drone_model,
    drone_type: identity.drone_type,
    lat,
    lng,
    heading,
    heading_known: headingKnown,
    asl: (mapped.asl as number | null) ?? null,
    agl: aglM != null ? aglM * M_TO_FT : null,
    aglM,
    atl: atlM != null ? atlM * M_TO_FT : null,
    atlM,
    // Airframe state lives on the telemetry topic — left null here so
    // mergeNonNull doesn't wipe it on every 1.7 Hz networkid tick.
    battery_pct: null,
    battery_v: null,
    flight_mode: null,
    flight_time: null,
    gps_sats: null,
    gps_hdop: (mapped.gps_hdop as number | null) ?? null,
    gps_lock: (mapped.gps_lock as TelemetryEnvelope['gps_lock']) ?? null,
    groundspeed: (mapped.groundspeed as number | null) ?? null,
    climbRate: (mapped.climbRate as number | null) ?? null,
    acceleration: null,
    signal: null,
    ops_status: (mapped.ops_status as TelemetryEnvelope['ops_status']) ?? null,
    status: null,
    status_label: null,
    confidence: null,
    proximity: null,
    conflict: null,
    pred1min,
    nmacRadius: identity.nmacRadius,
    swcRadius: identity.swcRadius,
    evalRadius: identity.evalRadius,
    flight_phase: (mapped.flight_phase as string | null) ?? null,
    height_type: (mapped.height_type as string | null) ?? null,
    vert_accuracy_m: (mapped.vert_accuracy_m as number | null) ?? null,
    speed_accuracy_mps: (mapped.speed_accuracy_mps as number | null) ?? null,
    timestamp_accuracy_s: (mapped.timestamp_accuracy_s as number | null) ?? null,
    self_id_text: (mapped.self_id_text as string | null) ?? null,
    track_origin: (mapped.track_origin as string | null) ?? null,
    ...(partnerTenant ? { origin: 'partner' as const, partner_tenant: partnerTenant } : {}),
    // The Remote Identification section in the Overview tab was built for
    // exactly this
    // payload and has never had a live feed — populate it.
    remote_id: {
      uas_id: droneId,
      operator_id: asText(p.operatorId),
      ua_type: identity.drone_type || null,
      // F3411 declares emergency through `operationalStatus` itself, so a
      // broadcast non-emergency phase genuinely means "no emergency". But
      // an ABSENT status means we don't know — it must not read as "None".
      emergency_status: emergencyStatus(p.operationalStatus),
      horiz_accuracy_m: horizAcc,
      vert_accuracy_m: accuracyMetres(p.verticalAccuracy),
      last_broadcast_at: receivedAt,
    },
    received_at: receivedAt,
  };

  const validated = TelemetryEnvelopeSchema.safeParse(built);
  if (!validated.success) {
    recordParseFailure(TOPIC_KIND, validated.error.issues[0]?.message ?? 'schema rejected');
    return null;
  }
  recordAccepted(TOPIC_KIND);
  return validated.data;
}

export interface RemoteIdOiUpdate {
  drone_id: string;
  partial: {
    pilot_name?: string | null;
    mission_name?: string | null;
    mission_location?: string | null;
    waypoints?: [number, number][] | null;
  };
}

/** Mission metadata carried in the `ext` block. Decorates an existing OI
 *  snapshot — it must never fabricate one, or every drone without a
 *  declared OI would render a ghost record. */
export function remoteIdToOiUpdate(raw: unknown): RemoteIdOiUpdate | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const droneId = asText(p.uasId);
  if (!droneId) return null;

  const ext = (p.ext ?? {}) as Record<string, unknown>;
  const partial: RemoteIdOiUpdate['partial'] = {};

  const pilot = asText(ext.pilotFullName);
  const missionName = asText(ext.missionName);
  const missionLocation = asText(ext.missionLocation);
  const waypoints = waypointsToLngLat(ext.waypoints);

  // `selfIdText` is deliberately NOT copied into `mission_notes`. It is
  // the F3411 free-text operator description, a different field with a
  // different meaning; it happens to hold the same string as
  // `mission_notes` on the two sandbox aircraft, but one publisher's
  // coincidence is not a contract. The value is kept verbatim on the
  // telemetry envelope's `self_id_text`.

  if (pilot) partial.pilot_name = pilot;
  if (missionName) partial.mission_name = missionName;
  if (missionLocation) partial.mission_location = missionLocation;
  if (waypoints) partial.waypoints = waypoints;

  return Object.keys(partial).length > 0 ? { drone_id: droneId, partial } : null;
}
